import { Link } from "@tanstack/react-router";
import { AlertCircle, UsersRound } from "lucide-react";

import { AppShell } from "components/AppShell";
import { EmptyState, LoadingRows } from "components/states";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

import { usePublicEvent } from "../events/use-public-event";
import { ConnectResult } from "./ConnectResult";
import { ctaButtonClass } from "./button-styles";
import { formatCode, normalizeCode } from "./connect-code";
import { useConnectionRequest } from "./use-connection-request";

/** What the rules answer when the caller may not file CONNECT there. */
function friendlyError(error: string): string {
  return /permission/i.test(error)
    ? "You can connect only at an event you are registered for, while its organisers have connections switched on."
    : error;
}

/**
 * /connect/{code}?e={slug}: where a phone's camera app lands after reading
 * somebody's connect QR (TNG-93).
 *
 * It asks before connecting. Opening a link is not the same as choosing to
 * hand over your card, and a link like this can arrive in a chat from anyone;
 * one tap keeps the scan quick and the choice the person's.
 */
export function ConnectLinkPage({
  uid,
  email,
  signOut,
  code: rawCode,
  slug,
}: {
  uid: string;
  email: string | null;
  signOut: () => void;
  code: string;
  slug?: string;
}) {
  const code = normalizeCode(rawCode);
  return (
    <AppShell identity={{ email }} onSignOut={signOut} title="Connect">
      <div className="mx-auto max-w-md">
        {!code ? (
          <EmptyState
            icon={AlertCircle}
            title="This is not a connect code"
            testId="connect-link-invalid"
          >
            Ask the person to show their code again, or type it in from the
            Connect card of the event.
          </EmptyState>
        ) : !slug ? (
          <EmptyState
            icon={AlertCircle}
            title="Which event?"
            testId="connect-link-no-event"
          >
            This link does not name an event. Open the event you are at and scan
            or type the code from its Connect card.
          </EmptyState>
        ) : (
          <ConnectAtEvent uid={uid} code={code} slug={slug} />
        )}
      </div>
    </AppShell>
  );
}

function ConnectAtEvent({
  uid,
  code,
  slug,
}: {
  uid: string;
  code: string;
  slug: string;
}) {
  const event = usePublicEvent(slug);
  const request = useConnectionRequest(uid);

  if (event.status === "loading") return <LoadingRows rows={3} />;
  if (event.status !== "success") {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Event not found"
        testId="connect-link-event-missing"
      >
        This event may have been unpublished or the link may be incorrect.
      </EmptyState>
    );
  }
  if (event.event.configConnections !== true) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Connections are off"
        testId="connect-link-off"
      >
        The organisers of {event.event.title} have not switched connections on.
      </EmptyState>
    );
  }

  const state =
    request.state.status === "failed"
      ? { ...request.state, error: friendlyError(request.state.error) }
      : request.state;

  return (
    <Card className="border-border" data-testid="connect-link">
      <CardContent className="space-y-4 py-6">
        <div className="flex items-start gap-3">
          <UsersRound
            className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <p className="text-foreground">
            Connect with the owner of code{" "}
            <span className="font-mono font-semibold">{formatCode(code)}</span>{" "}
            at <span className="font-semibold">{event.event.title}</span>? You
            both get each other&apos;s card: name, role, company and photo, and
            the email and LinkedIn you each share.
          </p>
        </div>
        {state.status === "idle" ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className={ctaButtonClass}
              onClick={() =>
                request.submit("CONNECT", {
                  code,
                  eventId: event.event.eventId,
                })
              }
              data-testid="connect-link-confirm"
            >
              Connect
            </Button>
            <Button asChild variant="outline">
              <Link to="/e/$slug" params={{ slug }}>
                Not now
              </Link>
            </Button>
          </div>
        ) : (
          <ConnectResult state={state} />
        )}
      </CardContent>
    </Card>
  );
}
